// Plans and verified domains (ADR-0030, api.md 2.7). Display names and texts only: every limit number comes
// from the server (`limits` on tenant detail), never from the console.
import type { Domain, DomainState, LimitUse, Plan, TenantDetail, TenantLimits } from "../api/types";
import { formatBytes } from "./format";

/** Plan display names (docs/glossary.md). An old `starter` shows as Organization: repo migration 0008 makes it `org`. */
const PLAN_NAMES: Record<Plan, string> = {
  trial: "Trial",
  indie: "Indie",
  org: "Organization",
  publisher: "Publisher",
  consumer: "Consumer",
  starter: "Organization",
};

export function planName(plan: Plan | string): string {
  return (PLAN_NAMES as Record<string, string>)[plan] ?? plan;
}

/** The plan, with "until <date>" for a trial that has an end date. */
export function planLine(t: Pick<TenantDetail, "plan" | "trial_ends_at">): string {
  const name = planName(t.plan);
  return t.plan === "trial" && t.trial_ends_at ? `${name} until ${t.trial_ends_at.slice(0, 10)}` : name;
}

/** The same value as DOMAIN_RULES.lapseGraceDays in apps/registry/src/plans.ts (a domain rule, not a plan limit). */
export const DOMAIN_LAPSE_GRACE_DAYS = 30;

/** End of the grace of a lapsed domain (`lapsed_at` + 30 days), as RFC 3339, or null when it is not lapsed. */
export function graceEnd(d: Pick<Domain, "lapsed_at">): string | null {
  if (!d.lapsed_at) return null;
  const t = Date.parse(d.lapsed_at);
  if (Number.isNaN(t)) return null;
  return new Date(t + DOMAIN_LAPSE_GRACE_DAYS * 86_400_000).toISOString().replace(/\.\d+Z$/, "Z");
}

export const DOMAIN_STATE_TEXT: Record<DomainState, string> = {
  pending: "Pending: add the TXT record, then verify.",
  verified: "Verified.",
  lapsed: "Lapsed: it still counts until the grace ends. Verify it again.",
  unverified: "Unverified: it does not count.",
};

/** Domains that count for the plan rules: verified, or lapsed and still in grace (ADR-0030 section 4.1). */
export function countingDomains(domains: Domain[] | undefined): string[] {
  return (domains ?? []).filter((d) => d.state === "verified" || d.state === "lapsed").map((d) => d.domain);
}

/** Lapsed domains, for the owner banner. */
export function lapsedDomains(domains: Domain[] | undefined): Domain[] {
  return (domains ?? []).filter((d) => d.state === "lapsed");
}

/** "@acme.com" or "@acme.com or @acme.io". */
function atList(domains: string[]): string {
  return domains.map((d) => `@${d}`).join(" or ");
}

export const INDIE_INVITES_TEXT =
  "The Indie plan has one member, the owner. To add members, ask Katskill to change the plan.";

/** Hint for the invite form, or null when the plan has no member rule. */
export function inviteHint(t: TenantDetail): string | null {
  if (t.plan === "indie") return INDIE_INVITES_TEXT;
  if (t.plan === "org" || t.plan === "starter") {
    const domains = countingDomains(t.domains);
    return domains.length
      ? `Organization plan: only addresses on ${atList(domains)} (or a subdomain).`
      : "Organization plan: verify a domain in Settings first. Members must have an address on it.";
  }
  return null;
}

/** Hint under the tenant slug of a grant to another tenant, or null when any tenant is allowed. */
export function tenantGrantHint(t: TenantDetail): string | null {
  if (t.plan === "org" || t.plan === "starter") {
    const domains = countingDomains(t.domains);
    return domains.length
      ? `Organization plan: only tenants that verified ${domains.join(" or ")} too. Sharing with other organizations needs the Publisher plan.`
      : "Organization plan: verify a domain in Settings first. Then you can grant to tenants that verified the same domain.";
  }
  return null;
}

/** True when the plan cannot grant to other tenants at all (Indie, Consumer). Uses `limits` when the server sends it. */
export function noTenantGrants(t: TenantDetail): boolean {
  if (isTenantLimits(t.limits)) return t.limits.tenant_grants === "none";
  return t.plan === "indie" || t.plan === "consumer";
}

export const PAUSED_TEXT: Record<"plan" | "domain", string> = {
  plan: "Paused by plan: the plan of this tenant does not allow this grant now. It works again after an upgrade.",
  domain:
    "Paused by domain: the two tenants do not share a verified domain now. It works again after a re-verify.",
};

/** "12 / 16", or "12" when there is no plan limit. Bytes for storage. */
export function limitText(u: LimitUse, bytes = false): string {
  const f = (n: number) => (bytes ? formatBytes(n) : String(n));
  return u.max === null ? f(u.used) : `${f(u.used)} / ${f(u.max)}`;
}

function isLimitUse(x: unknown): x is LimitUse {
  const u = x as LimitUse | null;
  return (
    typeof u === "object" &&
    u !== null &&
    typeof u.used === "number" &&
    (u.max === null || typeof u.max === "number")
  );
}

/**
 * True when `x` has the `limits` shape of ADR-0030 section 8. The console ignores `limits` of another shape (the
 * usage card then keeps the summary), and the mock fixture test fails, so a registry mismatch shows at once.
 */
export function isTenantLimits(x: unknown): x is TenantLimits {
  const l = x as Record<string, unknown> | null;
  return (
    typeof l === "object" &&
    l !== null &&
    typeof l.plan === "string" &&
    isLimitUse(l.members) &&
    isLimitUse(l.principals) &&
    isLimitUse(l.namespaces) &&
    isLimitUse(l.storage_bytes) &&
    (l.tenant_grants === "any" || l.tenant_grants === "same_domain" || l.tenant_grants === "none")
  );
}
